import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Truck, DollarSign, Plus, MapPin, Activity, Loader2,
  ShieldAlert, Clock, CheckCircle2, ArrowRight, Star,
  BarChart2, Users, Package, Settings, LogOut,
  Bell, Search, MessageCircle, ChevronDown, Building2,
  TrendingUp, FileText, Zap,
} from "lucide-react";

// ─── NAV ITEMS (matches Dispatcher + Driver sidebar structure) ─────────────────

const NAV_ITEMS = [
  { key: "dashboard",  label: "Dashboard",    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6", path: "/company-dashboard"},
  { key: "loads",      label: "My Loads",     icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", badge: "Live", path: "/my-loads"},
  { key: "drivers",    label: "Drivers",      icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
  { key: "analytics",  label: "Analytics",    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z", path: "/analytics"},
  { key: "documents",  label: "Documents",    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
  { key: "messages",   label: "Messages",     icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z", badge: 2 },
  { key: "settings",   label: "Settings",     icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" },
];

// ─── SVG Icon helper (matches dispatcher/driver pattern) ──────────────────────

function Icon({ d, size = "w-5 h-5", stroke = 1.8 }) {
  return (
    <svg className={`${size} flex-shrink-0`} fill="none" stroke="currentColor" strokeWidth={stroke} viewBox="0 0 24 24">
      {d.split(" M").map((seg, i) => (
        <path key={i} strokeLinecap="round" strokeLinejoin="round" d={i === 0 ? seg : "M" + seg} />
      ))}
    </svg>
  );
}

// ─── Status Pill (matches driver's pill system) ────────────────────────────────

function StatusPill({ status, isOpen }) {
  if (isOpen) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
        <Clock className="w-2.5 h-2.5" /> Pending Match
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 whitespace-nowrap">
      <CheckCircle2 className="w-2.5 h-2.5" /> Assigned
    </span>
  );
}

// ─── Stat Card (exact match to driver's stat-card) ────────────────────────────

function StatCard({ label, value, sub, icon: Icon, iconBg, trend, trendUp }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className={`text-xs font-semibold ${trendUp ? "text-green-600" : "text-red-500"}`}>
            {trend}
          </span>
        )}
      </div>
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Load Detail Modal (createPortal, matching driver's consent modal style) ──

function LoadModal({ load, onClose, viewerRole = "company" }) {
  if (!load) return null;

  // Field normalisers — PostLoadPage uses camelCase; API returns snake_case
  const isOpen      = load.status?.toLowerCase() === "open";
  const isAssigned  = !isOpen;

  const commodity   = load.commodity || load.load_type || load.loadType || "—";
  const weight      = load.weight || (load.weight_lbs ? `${load.weight_lbs} lbs` : null) || "N/A";
  const width       = load.width  || load.width_ft  || "—";
  const height      = load.height || load.height_ft || "—";
  const len         = load.length || load.length_ft || "—";
  const fee         = parseFloat(load.posting_fee || load.fee || 0).toFixed(2);
  const originZip   = load.origin_zip      || load.originZip      || "";
  const destZip     = load.destination_zip || load.destinationZip || "";
  const hasEscort   = load.needs_front_escort || load.needs_chase_escort
                   || load.needs_height_pole  || load.needs_steersman;
  const hasMeeting  = load.meeting_point || load.meetingPoint || load.meeting_address;
  const showMeeting = viewerRole === "company" || isAssigned;
  const showZip     = viewerRole === "company" || isAssigned;

  const depDate = load.departure_date
    ? new Date(load.departure_date).toLocaleDateString("en-US", {
        weekday:"long", year:"numeric", month:"long", day:"numeric"
      })
    : "—";
  const depTime = load.departure_time || load.time || "";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background:"rgba(0,0,0,0.65)", backdropFilter:"blur(3px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ background:"#1a2744" }}>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-white">Load Details</h3>
              {load.is_featured && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                  ⚡ Featured
                </span>
              )}
            </div>
            <p className="text-[10px] text-white/40 mt-0.5">
              #{load.id} · {isOpen ? "Pending Driver Match" : "Driver Assigned"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[9px] font-bold px-2 py-1 rounded-full border ${
              isOpen
                ? "bg-amber-400/15 text-amber-300 border-amber-400/30"
                : "bg-green-400/15 text-green-300 border-green-400/30"
            }`}>
              {isOpen ? "⏳ Pending" : "✅ Assigned"}
            </span>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white text-lg transition-colors"
            >✕</button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* ROUTE */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">Route</p>
            <div className="flex items-start gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] text-gray-400 font-medium mb-0.5">From</p>
                <p className="text-base font-black text-gray-900 leading-snug truncate">{load.origin}</p>
                {showZip && originZip && (
                  <p className="text-[10px] text-gray-400 font-mono mt-0.5">ZIP {originZip}</p>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-4" strokeWidth={2.5} />
              <div className="flex-1 min-w-0 text-right">
                <p className="text-[9px] text-gray-400 font-medium mb-0.5">To</p>
                <p className="text-base font-black text-gray-900 leading-snug truncate">{load.destination}</p>
                {showZip && destZip && (
                  <p className="text-[10px] text-gray-400 font-mono mt-0.5">ZIP {destZip}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
              <span>📅 {depDate}</span>
              {depTime && <span>· 🕐 {depTime}</span>}
            </div>
          </div>

          {/* LOAD SPECS */}
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">Load Specs</p>
            <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
              {/* Commodity — full width row */}
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">📦 Commodity</p>
                <p className="text-sm font-bold text-gray-900">{commodity}</p>
              </div>
              {/* Dimensions grid */}
              <div className="grid grid-cols-3 divide-x divide-gray-100">
                {[
                  { label:"Width",  val: width  },
                  { label:"Height", val: height },
                  { label:"Length", val: len    },
                ].map(({ label, val }) => (
                  <div key={label} className="px-3 py-3 text-center">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
                    <p className="text-xs font-bold text-gray-900">{val}</p>
                  </div>
                ))}
              </div>
              {/* Weight + Fee row */}
              <div className="grid grid-cols-2 divide-x divide-gray-100 border-t border-gray-100">
                <div className="px-4 py-3">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">⚖️ Weight</p>
                  <p className="text-sm font-bold text-gray-900">{weight}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">💰 Fee Paid</p>
                  <p className="text-sm font-bold text-green-600">${fee}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ESCORT REQUIREMENTS — premium visual grid */}
          <div>
            {/* Section header */}
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
                Escort Requirements
              </p>
              {/* Active count badge */}
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                hasEscort
                  ? "bg-red-100 text-red-700 border border-red-200"
                  : "bg-gray-100 text-gray-500 border border-gray-200"
              }`}>
                {hasEscort
                  ? `${[load.needs_front_escort, load.needs_chase_escort, load.needs_height_pole, load.needs_steersman].filter(Boolean).length} Required`
                  : "Standard Only"
                }
              </span>
            </div>

            {/* 2×2 escort card grid — all 4 always shown, active ones highlighted */}
            <div className="grid grid-cols-2 gap-2">
              {[
                {
                  key:    "needs_front_escort",
                  active: load.needs_front_escort,
                  label:  "Front Escort",
                  sub:    "Lead vehicle ahead of load",
                  icon:   "🚗",
                  svg:    "M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0",
                  activeColor: "#1d4ed8",
                  activeBg:    "#eff6ff",
                  activeBorder:"#bfdbfe",
                },
                {
                  key:    "needs_chase_escort",
                  active: load.needs_chase_escort,
                  label:  "Chase Escort",
                  sub:    "Follow vehicle behind load",
                  icon:   "🚙",
                  svg:    "M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0",
                  activeColor: "#1d4ed8",
                  activeBg:    "#eff6ff",
                  activeBorder:"#bfdbfe",
                },
                {
                  key:    "needs_height_pole",
                  active: load.needs_height_pole,
                  label:  "Height Pole",
                  sub:    "Overhead clearance detection",
                  icon:   "📡",
                  svg:    "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
                  activeColor: "#b45309",
                  activeBg:    "#fffbeb",
                  activeBorder:"#fde68a",
                },
                {
                  key:    "needs_steersman",
                  active: load.needs_steersman,
                  label:  "Steersman",
                  sub:    "Rear steering operator",
                  icon:   "🧑‍✈️",
                  svg:    "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
                  activeColor: "#6d28d9",
                  activeBg:    "#f5f3ff",
                  activeBorder:"#ddd6fe",
                },
              ].map(e => (
                <div
                  key={e.key}
                  style={e.active ? {
                    background:   e.activeBg,
                    borderColor:  e.activeBorder,
                    borderWidth:  "1.5px",
                    borderStyle:  "solid",
                  } : {
                    background:   "#f9fafb",
                    borderColor:  "#e5e7eb",
                    borderWidth:  "1.5px",
                    borderStyle:  "dashed",
                    opacity:      0.55,
                  }}
                  className="rounded-xl p-3 relative overflow-hidden"
                >
                  {/* Active glow strip on left edge */}
                  {e.active && (
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
                      style={{ background: e.activeColor }}/>
                  )}

                  <div className="flex items-start gap-2.5">
                    {/* Icon circle */}
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                      style={e.active
                        ? { background: e.activeColor, boxShadow:`0 2px 8px ${e.activeColor}40` }
                        : { background: "#e5e7eb" }
                      }
                    >
                      {e.active ? (
                        <svg className="w-4.5 h-4.5" style={{ color:"#fff" }} fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d={e.svg}/>
                        </svg>
                      ) : (
                        <span style={{ fontSize:16, opacity:0.4 }}>{e.icon}</span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-bold" style={{ color: e.active ? e.activeColor : "#9ca3af" }}>
                          {e.label}
                        </span>
                        {e.active && (
                          <span className="text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded"
                            style={{ background: e.activeColor, color:"#fff" }}>
                            ✓ REQ
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] mt-0.5 leading-snug"
                        style={{ color: e.active ? "#374151" : "#9ca3af" }}>
                        {e.sub}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary callout when all 4 active */}
            {load.needs_front_escort && load.needs_chase_escort && load.needs_height_pole && load.needs_steersman && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200">
                <span className="text-sm">⚠️</span>
                <p className="text-[10px] font-bold text-red-700">
                  Full escort package required — all 4 escort types must be arranged before dispatch.
                </p>
              </div>
            )}

            {/* Standard only callout */}
            {!hasEscort && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                <span className="text-sm">✅</span>
                <p className="text-[10px] font-semibold text-gray-500">
                  Standard escort only — no special escort vehicles required.
                </p>
              </div>
            )}
          </div>

          {/* MEETING POINT */}
          {hasMeeting && (
            showMeeting ? (
              <div className={`rounded-xl p-3.5 border ${
                viewerRole === "driver" && isAssigned
                  ? "bg-green-50 border-green-200"
                  : "bg-blue-50/40 border-blue-100"
              }`}>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-2 text-blue-700 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3"/> Pickup / Meeting Point
                </p>
                {(load.meeting_point || load.meetingPoint) && (
                  <p className="text-sm font-semibold text-gray-900 mb-1.5">
                    {load.meeting_point || load.meetingPoint}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5 text-xs text-gray-600">
                  {(load.meeting_city  || load.meetingCity)  && <span>{load.meeting_city || load.meetingCity},</span>}
                  {(load.meeting_state || load.meetingState) && <span>{load.meeting_state || load.meetingState}</span>}
                  {(load.meeting_zip   || load.meetingZip)   && <span className="font-mono text-gray-500">{load.meeting_zip || load.meetingZip}</span>}
                </div>
                {(load.meeting_time || load.meetingTime) && (
                  <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">🕐 Meet at {load.meeting_time || load.meetingTime}</p>
                )}
                {viewerRole === "driver" && isAssigned && (
                  <p className="text-[10px] text-green-700 font-semibold mt-2 flex items-center gap-1">
                    ✅ Pickup location revealed — you are assigned to this load
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-xl p-3.5 border border-gray-200 bg-gray-50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-base">🔒</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-700">Pickup Location Hidden</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 leading-relaxed">
                    Exact pickup address will be revealed once you are assigned to this load.
                  </p>
                </div>
              </div>
            )
          )}

          {/* NOTES — company only */}
          {viewerRole === "company" && (load.notes || load.special_instructions) && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">📝 Notes</p>
              <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-3 border border-gray-100">
                {load.notes || load.special_instructions}
              </p>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="border border-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <div className="flex gap-2">
            {viewerRole === "company" && isOpen && (
              <button
                className="text-xs font-semibold px-3 py-2 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                onClick={onClose}
              >
                Edit Load
              </button>
            )}
            <button
              className="text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors hover:opacity-90"
              style={{ background:"#1a2744" }}
              onClick={onClose}
            >
              {viewerRole === "driver"
                ? (isAssigned ? "View Route" : "Apply for Load")
                : (isOpen ? "View Applicants" : "View Driver Info")
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function LoadCard({ load, onViewDetails }) {
  const isOpen = load.status?.toLowerCase() === "open";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" }) : "—";

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
      onClick={() => onViewDetails(load)}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
        {/* Left info */}
        <div className="flex-1 min-w-0">
          {/* Badges */}
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            <StatusPill status={load.status} isOpen={isOpen} />
            {load.is_featured && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                <Star className="w-2.5 h-2.5" /> Featured
              </span>
            )}
          </div>

          {/* Route */}
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-bold text-gray-900 truncate">{load.origin}</span>
            <ArrowRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" strokeWidth={2.5} />
            <span className="text-sm font-bold text-gray-900 truncate">{load.destination}</span>
          </div>

          {/* Meta */}
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <Truck className="w-3 h-3" />
            {load.commodity || load.load_type || load.loadType || "—"}
            <span className="text-gray-200">•</span>
            {fmtDate(load.departure_date)}
          </p>

          {/* Escort tags */}
          {(load.needs_front_escort || load.needs_chase_escort || load.needs_height_pole) && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {load.needs_front_escort && <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">Front Escort</span>}
              {load.needs_chase_escort && <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">Chase Escort</span>}
              {load.needs_height_pole  && <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">Height Pole</span>}
            </div>
          )}
        </div>

        {/* Right — fee + button */}
        <div className="flex sm:flex-col items-center sm:items-end justify-between gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l border-gray-100 sm:pl-5 flex-shrink-0">
          <div className="sm:text-right">
            <p className="text-[9px] text-gray-400 font-medium uppercase tracking-wider">Marketplace Fee</p>
            <p className="text-base font-bold text-gray-900 mt-0.5">
              ${parseFloat(load.posting_fee || 0).toFixed(2)}
            </p>
          </div>
          <button
            onClick={e => { e.stopPropagation(); onViewDetails(load); }}
            className="text-xs font-bold px-3 py-1.5 rounded-xl transition-all"
            style={isOpen
              ? { border: "1px solid #e5e7eb", background: "#fff", color: "#374151" }
              : { background: "#1a2744", color: "#fff" }
            }
          >
            {isOpen ? "View Status" : "Driver Info"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-gray-200 rounded-2xl">
      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
        <MapPin className="w-7 h-7 text-gray-300" />
      </div>
      <h3 className="text-sm font-bold text-gray-800 mb-1">No Active Loads</h3>
      <p className="text-xs text-gray-400 max-w-xs leading-relaxed mb-5">
        You haven't posted any oversize loads yet. Create your first listing to instantly dispatch to our certified driver network.
      </p>
      <Link to="/post-load">
        <button className="text-white text-xs font-bold px-4 py-2.5 rounded-xl flex items-center gap-2" style={{ background: "#1a2744" }}>
          <Plus className="w-4 h-4" /> Post Your First Load
        </button>
      </Link>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CompanyDashboard({
  nav = (r) => console.log("Navigate:", r),
}) {
  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();

  const [loads,        setLoads]        = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [activeNav,    setActiveNav]    = useState("dashboard");
  const [loadFilter,   setLoadFilter]   = useState("all");
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [notifCount,   setNotifCount]   = useState(2);

  const companyName = user?.company_name || user?.name || "Company";
  const initials    = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  // Fetch loads
  useEffect(() => {
    const fetchLoads = async () => {
      if (!user?.email) { setIsLoading(false); return; }
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token") || "";

        // ── Correct endpoint: loads/company/ (matches urls.py ListCompanyLoadsAPIView)
        // ── Authorization header required — without it the server returns a 401/404 HTML page
        const res = await fetch(
          `${apiUrl}/api/loads/company/?email=${encodeURIComponent(user.email)}`,
          {
            headers: {
              "Content-Type":  "application/json",
              ...(token ? { "Authorization": `Bearer ${token}` } : {}),
            },
          }
        );

        // Guard: if server returned HTML (login redirect / 404 page) instead of JSON
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          console.error("API returned non-JSON response. Status:", res.status,
            "— check REACT_APP_API_URL and that the user token is valid.");
          setIsLoading(false);
          return;
        }

        const data = await res.json();
        if (res.ok) {
          setLoads(data.results || data || []);
        } else {
          console.error("Failed to fetch loads:", data);
        }
      } catch (err) {
        console.error("Network error fetching loads:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLoads();
  }, [user?.email]);

  // Derived stats
  const activePostings = loads.filter(l => l.status?.toLowerCase() === "open").length;
  const assignedLoads  = loads.filter(l => l.status?.toLowerCase() !== "open").length;
  const totalSpent     = loads.reduce((acc, l) => acc + parseFloat(l.posting_fee || 0), 0);

  // Filtered loads
  const filteredLoads = loadFilter === "all"      ? loads
    : loadFilter === "open"     ? loads.filter(l => l.status?.toLowerCase() === "open")
    : loads.filter(l => l.status?.toLowerCase() !== "open");

  const PAGE_TITLES = {
    dashboard: "Dashboard", loads: "My Loads", drivers: "Drivers",
    analytics: "Analytics", documents: "Documents", messages: "Messages", settings: "Settings",
  };

  // ── Non-dashboard stub ──
  if (activeNav !== "dashboard") {
    return (
      <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
        <Sidebar activeNav={activeNav} setActiveNav={setActiveNav} sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen} initials={initials} companyName={companyName}
          loads={loads} activePostings={activePostings} assignedLoads={assignedLoads} totalSpent={totalSpent} />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <TopBar title={PAGE_TITLES[activeNav]} setSidebarOpen={setSidebarOpen} initials={initials}
            notifCount={notifCount} setNotifCount={setNotifCount} />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Icon d={NAV_ITEMS.find(n => n.key === activeNav)?.icon || ""} size="w-7 h-7" />
              </div>
              <p className="text-sm font-bold text-gray-800 mb-1">{PAGE_TITLES[activeNav]}</p>
              <p className="text-xs text-gray-400 max-w-xs leading-relaxed">
                This section is handled by your existing React Router. It renders in production.
              </p>
              <button onClick={() => setActiveNav("dashboard")} className="mt-4 border border-gray-200 text-gray-600 text-xs font-semibold px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">
                ← Back to Dashboard
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <Sidebar
        activeNav={activeNav} setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName}
        loads={loads} activePostings={activePostings}
        assignedLoads={assignedLoads} totalSpent={totalSpent}
      />

      {/* ── MAIN ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* ── TOPBAR ── */}
        <TopBar
          title="Dashboard"
          setSidebarOpen={setSidebarOpen}
          initials={initials}
          notifCount={notifCount}
          setNotifCount={setNotifCount}
        />

        {/* ── CONTENT ── */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 space-y-5">

            {/* Welcome banner — navy like driver's */}
            <div className="rounded-2xl p-5 flex items-center justify-between gap-4" style={{ background: "#1a2744" }}>
              <div>
                <h2 className="text-white font-bold text-base">
                  Welcome back, {companyName}! 👋
                </h2>
                <p className="text-blue-200 text-sm mt-1">
                  {activePostings > 0
                    ? `${activePostings} load${activePostings !== 1 ? "s" : ""} awaiting driver match.`
                    : "Post a load to get started with dispatching."
                  }
                </p>
              </div>
              <Link to="/post-load" className="flex-shrink-0">
                <button className="flex items-center gap-1.5 bg-white/15 border border-white/25 text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-white/25 transition-colors whitespace-nowrap">
                  <Plus className="w-3.5 h-3.5" /> Post New Load
                </button>
              </Link>
            </div>

            {/* ── STAT CARDS ── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard
                label="Active Postings"
                value={activePostings}
                sub="Awaiting match"
                icon={Activity}
                iconBg="bg-blue-50 text-blue-600"
                trend="↑ 12%"
                trendUp={true}
              />
              <StatCard
                label="Assigned Drivers"
                value={assignedLoads}
                sub="All time"
                icon={Truck}
                iconBg="bg-green-50 text-green-600"
                trend="↑ 5%"
                trendUp={true}
              />
              {/* <StatCard
                label="Marketplace Fees"
                value={`$${totalSpent.toFixed(2)}`}
                sub="Total spent"
                icon={DollarSign}
                iconBg="bg-indigo-50 text-indigo-600"
              /> */}
                   {/* Post Load CTA */}
                <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-blue-400/10 blur-xl pointer-events-none" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "#1a2744" }}>
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">Dispatch a Load</h3>
                    <p className="text-xs text-gray-400 leading-relaxed mb-4">
                      Instantly broadcast your oversize route to our nationwide network of certified pilot car professionals.
                    </p>
                    <Link to="/post-load">
                      <button
                        className="w-full text-white text-xs font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors hover:opacity-90"
                        style={{ background: "#1a2744" }}
                      >
                        <Plus className="w-4 h-4" /> Post New Load
                      </button>
                    </Link>
                  </div>
                </div>
            </div>

            {/* ── MAIN GRID ── */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

              {/* Left: Load list */}
              <div className="xl:col-span-2 space-y-4">
                {/* Section header with filter */}
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-gray-900">Your Recent Postings</h2>
                  <div className="flex gap-1.5">
                    {["all", "open", "assigned"].map(f => (
                      <button
                        key={f}
                        onClick={() => setLoadFilter(f)}
                        className="px-3 py-1 rounded-full text-[10px] font-semibold capitalize transition-colors"
                        style={loadFilter === f
                          ? { background: "#1a2744", color: "#fff" }
                          : { background: "#f3f4f6", color: "#6b7280" }
                        }
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <p className="text-xs text-gray-400 font-medium">Syncing your dashboard…</p>
                  </div>
                ) : filteredLoads.length === 0 ? (
                  <EmptyState />
                ) : (
                  <div className="space-y-3">
                    {filteredLoads.map(load => (
                      <LoadCard
                        key={load.id}
                        load={load}
                        onViewDetails={l => setSelectedLoad(l)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Sidebar widgets */}
              <div className="space-y-4">

           

                {/* Load Summary */}
                {/* <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                  <h3 className="text-xs font-bold text-gray-900 mb-3">Load Summary</h3>
                  {[
                    ["Total Posted",   loads.length],
                    ["Pending Match",  activePostings],
                    ["Assigned",       assignedLoads],
                    ["Total Fees",     `$${totalSpent.toFixed(2)}`],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                      <span className="text-[10px] text-gray-400">{label}</span>
                      <span className="text-[10px] font-bold text-gray-900">{val}</span>
                    </div>
                  ))}
                </div> */}

                {/* Support card */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                      <ShieldAlert className="w-4.5 h-4.5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Need Support?</p>
                      <p className="text-[10px] text-gray-400 mt-1 leading-relaxed mb-2">
                        Questions about compliance or dispatching? Our team is available 24/7.
                      </p>
                      <span className="text-[10px] font-bold text-blue-600 cursor-pointer hover:underline">
                        Contact Dispatch Team →
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick stats — earnings-style */}
                {/* <div className="rounded-2xl p-4 text-white" style={{ background: "#1a2744" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-blue-300" />
                    <p className="text-xs font-bold text-white">Quick Stats</p>
                  </div>
                  {[
                    ["Total Posted",   loads.length],
                    ["Pending Match",  activePostings],
                    ["Assigned",       assignedLoads],
                    ["Total Spent",    `$${totalSpent.toFixed(2)}`],
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between py-1.5 border-b border-white/10 last:border-0">
                      <span className="text-[10px] text-white/50">{l}</span>
                      <span className="text-[10px] font-bold text-white">{v}</span>
                    </div>
                  ))}
                </div> */}
              </div>
            </div>
          </div>
        </main>

        {/* ── MOBILE BOTTOM NAV ── */}
        <nav
          className="lg:hidden flex-shrink-0 bg-white border-t border-gray-200 flex"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {[
            { key:"dashboard", label:"Home",      icon:"M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
            { key:"loads",     label:"Loads",     icon:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
            { key:"drivers",   label:"Drivers",   icon:"M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
            { key:"messages",  label:"Messages",  icon:"M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" },
            { key:"settings",  label:"Settings",  icon:"M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setActiveNav(item.key)}
              className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors ${activeNav === item.key ? "text-blue-600" : "text-gray-400"}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                {item.icon.split(" M").map((seg, i) => (
                  <path key={i} strokeLinecap="round" strokeLinejoin="round" d={i === 0 ? seg : "M" + seg} />
                ))}
              </svg>
              <span className="text-[9px] font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Modal */}
      {selectedLoad && (
        <LoadModal load={selectedLoad} onClose={() => setSelectedLoad(null)} />
      )}
    </div>
  );
}

// ─── Sidebar sub-component ────────────────────────────────────────────────────

function Sidebar({ activeNav, setActiveNav, sidebarOpen, setSidebarOpen, initials, companyName, loads, activePostings, assignedLoads, totalSpent }) {
  return (
    <aside
      className={`fixed lg:relative z-50 lg:z-auto top-0 left-0 h-full w-56 flex-shrink-0 flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      style={{ background: "#1a2744" }}
    >
      {/* Logo — identical to dispatcher */}
      <div className="flex items-center gap-2.5 px-4 py-4 border-b border-white/10 flex-shrink-0">
        <div className="w-9 h-9 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <span className="font-bold text-white text-base tracking-wide">COMPANY</span>
          <p className="text-[9px] text-white/40 uppercase tracking-widest mt-0.5">PilotLinkUSA</p>
        </div>
        <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-white/50 hover:text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Company chip — identical to driver's MR chip */}
      <div className="mx-3 mt-3 rounded-xl p-2.5 flex items-center gap-2.5 flex-shrink-0" style={{ background: "rgba(255,255,255,0.07)" }}>
        <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center font-bold text-white text-xs flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-white truncate">{companyName}</p>
          <p className="text-[9px] text-white/40 truncate">Company Account</p>
        </div>
      </div>

      {/* Nav — identical structure */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
        <p className="px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-white/30">Main Menu</p>
        {NAV_ITEMS.map(({ key, label, icon, badge }) => (
          <button
            key={key}
            onClick={() => { setActiveNav(key); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeNav === key ? "bg-blue-600 text-white" : "text-gray-300 hover:bg-white/10 hover:text-white"}`}
          >
            <Icon d={icon} size="w-4 h-4" />
            <span className="truncate">{label}</span>
            {badge && (
              <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${activeNav === key ? "bg-white/20 text-white" : typeof badge === "number" ? "bg-red-500 text-white" : "bg-white/10 text-white/60"}`}>
                {badge}
              </span>
            )}
          </button>
        ))}

        <div className="border-t border-white/10 my-2" />
        <p className="px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-white/30">Account</p>
        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/10 transition-all">
          <LogOut className="w-4 h-4 flex-shrink-0" />
          <span>Sign Out</span>
        </button>
      </nav>

      {/* Today Summary — identical to driver's */}
      <div className="mx-3 mb-3 rounded-xl p-3 border border-white/10 flex-shrink-0" style={{ background: "rgba(255,255,255,0.05)" }}>
        <p className="text-[9px] font-bold text-white/30 uppercase tracking-widest mb-2">Today's Summary</p>
        {[
          ["Total Loads",    loads.length],
          ["Pending Match",  activePostings],
          ["Assigned",       assignedLoads],
          ["Total Fees",     `$${totalSpent.toFixed(2)}`],
        ].map(([l, v]) => (
          <div key={l} className="flex justify-between items-center py-0.5">
            <span className="text-[10px] text-gray-400">{l}</span>
            <span className="text-[10px] font-bold text-white">{v}</span>
          </div>
        ))}
      </div>

      {/* CTA — identical to driver's "Find Loads" */}
      <div className="px-3 pb-4 flex-shrink-0">
        <Link to="/post-load">
          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Post New Load
          </button>
        </Link>
      </div>
    </aside>
  );
}

// ─── TopBar sub-component ─────────────────────────────────────────────────────

function TopBar({ title, setSidebarOpen, initials, notifCount, setNotifCount }) {
  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center px-3 sm:px-5 gap-3 flex-shrink-0 z-30">
      <button onClick={() => setSidebarOpen(true)} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg lg:hidden">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <h1 className="text-lg font-bold text-gray-900">{title}</h1>

      {/* Search — identical to dispatcher */}
      <div className="flex-1 max-w-sm mx-4 hidden sm:flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          placeholder="Search loads, drivers, routes..."
          className="flex-1 bg-transparent text-sm text-gray-600 placeholder-gray-300 outline-none min-w-0"
        />
      </div>

      <div className="ml-auto flex items-center gap-1 sm:gap-3">
        {/* Notifications */}
        <button
          onClick={() => setNotifCount(0)}
          className="relative w-9 h-9 hover:bg-gray-100 rounded-xl flex items-center justify-center text-gray-500 transition-colors"
        >
          <Bell className="w-5 h-5" />
          {notifCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {notifCount}
            </span>
          )}
        </button>

        {/* Messages */}
        <button className="w-9 h-9 hover:bg-gray-100 rounded-xl items-center justify-center text-gray-500 hidden sm:flex transition-colors">
          <MessageCircle className="w-5 h-5" />
        </button>

        {/* Post Load CTA — navy, matches driver's "Find Loads" */}
        <Link to="/post-load">
          <button
            className="hidden sm:flex items-center gap-1.5 text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors hover:opacity-90"
            style={{ background: "#1a2744" }}
          >
            <Plus className="w-3.5 h-3.5" /> Post Load
          </button>
        </Link>

        {/* Avatar */}
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: "#1a2744" }}>
            {initials}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-semibold text-gray-800 leading-tight">Company</p>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span className="text-xs text-green-600">Active</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
